/**
 * API Client for Checkout Platform
 */

import type {
  ApiResponse,
  CheckoutSessionResponse,
  CheckoutSession,
  Plan,
  Subscription,
  ProcessPaymentRequest,
  ProcessPaymentResponse,
} from '../types/api';
import { createSigningHeaders } from './request-signing';

/**
 * Get API base URL
 */
function getApiUrl(): string {
  const env = import.meta.env.VITE_ENVIRONMENT || 'development';

  if (env === 'production') {
    return import.meta.env.VITE_CHECKOUT_API_URL_PROD || 'https://api-checkout.aitomata.app';
  }

  if (env === 'staging') {
    return import.meta.env.VITE_CHECKOUT_API_URL_STAGING || 'https://staging-api-checkout.aitomata.app';
  }

  return import.meta.env.VITE_CHECKOUT_API_URL || 'http://localhost:8787';
}

/**
 * Get SaaS secret key for request signing
 * Used to sign frontend proxy requests with HMAC-SHA256
 */
function getSaasSecret(): string {
  return import.meta.env.VITE_SAAS_SECRET || 'dev-secret-key';
}

/**
 * Make API request
 * Note: No X-API-Key header needed - frontend uses backend proxy endpoints
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${getApiUrl()}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      success: false,
      error: {
        code: `HTTP_${response.status}`,
        message: response.statusText,
      },
    }));

    throw new Error(error.error?.message || response.statusText);
  }

  return response.json() as Promise<T>;
}

/**
 * Checkout API Client
 */
export const checkoutApi = {
  /**
   * Create checkout session
   * Uses backend proxy endpoint for security (no API key exposed in frontend)
   * Request is signed with HMAC-SHA256 to verify authenticity
   */
  async createSession(data: {
    organization_id: string;
    plan_id: string;
    saas: string;
    user_id: string;
    customer_email: string;
    customer_phone?: string;
    language?: string;
    metadata?: Record<string, unknown>;
  }): Promise<CheckoutSessionResponse> {
    const payload = JSON.stringify(data);
    const signingHeaders = await createSigningHeaders(payload, getSaasSecret());

    const response = await apiRequest<ApiResponse<CheckoutSessionResponse>>(
      '/api/checkout/proxy/sessions',
      {
        method: 'POST',
        body: payload,
        headers: signingHeaders,
      }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to create checkout session');
    }

    return response.data;
  },

  /**
   * Get checkout session
   * Uses public endpoint - only requires session_id (no authentication needed)
   * Session IDs are cryptographically secure random tokens
   */
  async getSession(sessionId: string): Promise<CheckoutSession> {
    const response = await apiRequest<ApiResponse<CheckoutSession>>(
      `/api/checkout/sessions/${sessionId}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get checkout session');
    }

    return response.data;
  },

  /**
   * Process payment and create subscription
   * Uses backend proxy endpoint for security (no API key exposed in frontend)
   * Request is signed with HMAC-SHA256 to verify authenticity
   */
  async processPayment(data: ProcessPaymentRequest): Promise<ProcessPaymentResponse> {
    const payload = JSON.stringify(data);
    const signingHeaders = await createSigningHeaders(payload, getSaasSecret());

    const response = await apiRequest<ApiResponse<ProcessPaymentResponse>>(
      '/api/checkout/proxy/process-payment',
      {
        method: 'POST',
        body: payload,
        headers: signingHeaders,
      }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to process payment');
    }

    return response.data;
  },

  /**
   * Get all plans
   */
  async getPlans(): Promise<Plan[]> {
    const response = await apiRequest<ApiResponse<Plan[]>>('/api/plans');

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get plans');
    }

    return response.data;
  },

  /**
   * Get specific plan
   */
  async getPlan(planId: string): Promise<Plan> {
    const response = await apiRequest<ApiResponse<Plan>>(`/api/plans/${planId}`);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get plan');
    }

    return response.data;
  },

  /**
   * Get subscription
   */
  async getSubscription(subscriptionId: string): Promise<Subscription> {
    const response = await apiRequest<ApiResponse<Subscription>>(
      `/api/subscriptions/${subscriptionId}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get subscription');
    }

    return response.data;
  },

  /**
   * List subscriptions for organization
   */
  async listSubscriptions(organizationId: string): Promise<Subscription[]> {
    const response = await apiRequest<ApiResponse<Subscription[]>>(
      `/api/subscriptions?organization_id=${encodeURIComponent(organizationId)}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to list subscriptions');
    }

    return response.data;
  },

  /**
   * Cancel subscription
   */
  async cancelSubscription(
    subscriptionId: string,
    options?: { reason?: string; cancel_at_period_end?: boolean }
  ): Promise<Subscription> {
    const response = await apiRequest<ApiResponse<Subscription>>(
      `/api/subscriptions/${subscriptionId}/cancel`,
      {
        method: 'POST',
        body: JSON.stringify(options || {}),
      }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to cancel subscription');
    }

    return response.data;
  },

  /**
   * Initiate card registration with Flow
   */
  async registerCard(
    sessionId: string,
    session: CheckoutSession
  ): Promise<{ registration_url: string; registration_token: string }> {
    // Generate session proof to verify caller has access to full session data
    const { generateSessionProof } = await import('./request-signing');
    const sessionProof = await generateSessionProof(
      session.session_id,
      session.organization_id,
      session.expires_at
    );

    const response = await apiRequest<ApiResponse<{ registration_url: string; registration_token: string }>>(
      '/api/checkout/register-card',
      {
        method: 'POST',
        body: JSON.stringify({
          session_id: sessionId,
          session_proof: sessionProof,
        }),
      }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to initiate card registration');
    }

    return response.data;
  },

  /**
   * Verify card registration and complete subscription
   */
  async verifyCardRegistration(
    sessionId: string,
    token: string
  ): Promise<{ subscription_id: string; flow_subscription_id: string }> {
    const response = await apiRequest<ApiResponse<{ subscription_id: string; flow_subscription_id: string }>>(
      '/api/checkout/register-card-callback',
      {
        method: 'POST',
        body: JSON.stringify({ session_id: sessionId, token }),
      }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to verify card registration');
    }

    return response.data;
  },

};

export default checkoutApi;
